import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { BarChart3, TrendingDown, RefreshCw, Cpu, HardDrive, Zap } from 'lucide-react';
import { Button } from './ui/button.jsx';

/**
 * ResourceUtilizationGraph - Real-time visualization showing:
 * - Current Requests: What pods ask for
 * - Optimized Requests: Rightsized values (usage + 30% buffer)  
 * - Actual Usage: Current resource consumption
 */
function ResourceUtilizationGraph({ clusterId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        if (!clusterId) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API}/clusters/${clusterId}/realtime-graph`);
            setData(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch realtime graph:', err);
            setError('Failed to load metrics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [clusterId]);

    if (!clusterId) {
        return (
            <div className="rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 p-8 text-center">
                <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500">Select a cluster to view resource utilization</p>
            </div>
        );
    }

    if (loading && !data) {
        return (
            <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 p-8">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-slate-200 rounded-lg w-1/3" />
                    <div className="h-32 bg-slate-200 rounded-xl" />
                    <div className="h-32 bg-slate-200 rounded-xl" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl bg-gradient-to-br from-red-50 to-red-100 border border-red-200 p-8 text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={fetchData} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                </Button>
            </div>
        );
    }

    const cpu = data?.cpu || { requests: 0, usage: 0, optimized: 0 };
    const memory = data?.memory || { requests: 0, usage: 0, optimized: 0 };
    const savings = data?.savings_potential || { cpu_pct: 0, memory_pct: 0 };

    const maxCpu = Math.max(cpu.requests, cpu.usage, cpu.optimized, 0.1);
    const maxMem = Math.max(memory.requests, memory.usage, memory.optimized, 0.1);

    return (
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 border-b border-slate-100">
                <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center text-lg">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        Resource Utilization vs Requests
                    </CardTitle>
                    <Button onClick={fetchData} variant="ghost" size="sm" disabled={loading} className="hover:bg-blue-50">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-500' : 'text-slate-500'}`} />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="p-6 space-y-8">
                {/* CPU Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                            <Cpu className="w-5 h-5 text-blue-500" />
                            CPU <span className="text-slate-400 font-normal">({cpu.unit || 'cores'})</span>
                        </h4>
                        {savings.cpu_pct > 0 && (
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium">
                                <TrendingDown className="w-4 h-4" />
                                {savings.cpu_pct.toFixed(0)}% savings available
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        {/* Requests Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Requests</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(cpu.requests / maxCpu) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{cpu.requests?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Optimized Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Optimized</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(cpu.optimized / maxCpu) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{cpu.optimized?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Usage Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Usage</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-400 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(cpu.usage / maxCpu) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{cpu.usage?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Memory Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                            <HardDrive className="w-5 h-5 text-purple-500" />
                            Memory <span className="text-slate-400 font-normal">({memory.unit || 'GB'})</span>
                        </h4>
                        {savings.memory_pct > 0 && (
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium">
                                <TrendingDown className="w-4 h-4" />
                                {savings.memory_pct.toFixed(0)}% savings available
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        {/* Requests Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Requests</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(memory.requests / maxMem) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{memory.requests?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Optimized Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Optimized</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(memory.optimized / maxMem) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{memory.optimized?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Usage Bar */}
                        <div className="group">
                            <div className="flex items-center gap-4">
                                <span className="w-20 text-sm text-slate-500">Usage</span>
                                <div className="flex-1 h-8 bg-slate-100 rounded-xl overflow-hidden shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-400 rounded-xl transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                        style={{ width: `${(memory.usage / maxMem) * 100}%` }}
                                    >
                                        <span className="text-xs font-semibold text-white drop-shadow-sm">{memory.usage?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center items-center gap-8 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-md bg-gradient-to-r from-blue-400 to-indigo-500" />
                        <span className="text-sm text-slate-600">Requests</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-md bg-gradient-to-r from-green-400 to-emerald-500" />
                        <span className="text-sm text-slate-600">Optimized</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-md bg-gradient-to-r from-amber-400 to-orange-500" />
                        <span className="text-sm text-slate-600">Actual Usage</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default ResourceUtilizationGraph;
